import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#0066cc', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#20c997', '#fd7e14', '#e83e8c'];

function Users() {
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [portFilter, setPortFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [ports, setPorts] = useState([]);
  const [page, setPage] = useState(1);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const limit = 15;

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (typeFilter) params.append('user_type', typeFilter);
      if (portFilter) params.append('port_id', portFilter);
      if (verifiedFilter) params.append('email_verified', verifiedFilter);
      const data = await api(`/admin/users?${params}`);
      setUsers(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPorts = async () => {
    try {
      const data = await api('/admin/ports');
      setPorts(data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await api('/admin/users/analytics');
      setAnalytics(data.data || data);
    } catch (err) {
      console.error(err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, [page, typeFilter, portFilter, verifiedFilter]);
  useEffect(() => { loadAnalytics(); loadPorts(); }, []);

  // Clear selection when page/filter changes
  useEffect(() => { setSelectedUsers([]); }, [page, typeFilter, portFilter, verifiedFilter, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const updateStatus = async (userId, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this user?`)) return;
    try {
      await api(`/admin/users/${userId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const verifyEmail = async (userId) => {
    if (!window.confirm('Manually verify this user\'s email?')) return;
    try {
      const result = await api(`/admin/users/${userId}/verify-email`, { method: 'POST' });
      alert(result.data?.message || 'Email verified');
      loadUsers();
    } catch (err) {
      alert('Failed to verify email: ' + err.message);
    }
  };

  const resendVerification = async (userId) => {
    try {
      const result = await api(`/admin/users/${userId}/resend-verification`, { method: 'POST' });
      alert(result.data?.message || 'Verification email sent');
    } catch (err) {
      alert('Failed to send verification email: ' + err.message);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const openEmailModal = () => {
    setEmailSubject('');
    setEmailBody('');
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    setSendingEmail(true);
    try {
      const result = await api('/admin/users/email', {
        method: 'POST',
        body: JSON.stringify({
          userIds: selectedUsers,
          subject: emailSubject,
          body: emailBody
        }),
      });
      alert(result.message);
      setShowEmailModal(false);
      setSelectedUsers([]);
    } catch (err) {
      alert('Failed to send email: ' + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const selectedUsersList = users.filter(u => selectedUsers.includes(u.id));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1>Users</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          {selectedUsers.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={openEmailModal}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span>Email {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}</span>
            </button>
          )}
          <button
            className="btn"
            onClick={() => setShowAnalytics(!showAnalytics)}
            style={{ background: showAnalytics ? '#0066cc' : '#eee', color: showAnalytics ? 'white' : '#333' }}
          >
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
        </div>
      </div>

      {/* Analytics Section */}
      {showAnalytics && (
        <div style={{ marginBottom: 24 }}>
          {analyticsLoading ? (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>Loading analytics...</div>
          ) : analytics ? (
            <>
              {/* Row 1: User Growth & Cumulative */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>Daily Registrations (Last 30 Days)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={analytics.userGrowth.map(d => ({
                      ...d,
                      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="owners" stackId="1" stroke="#0066cc" fill="#0066cc" fillOpacity={0.6} name="Owners" />
                      <Area type="monotone" dataKey="helpers" stackId="1" stroke="#28a745" fill="#28a745" fillOpacity={0.6} name="Helpers" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>Cumulative Users</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={analytics.cumulativeUsers.map(d => ({
                      ...d,
                      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="cumulative" stroke="#0066cc" strokeWidth={2} dot={{ r: 2 }} name="Total Users" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Row 2: Helpers by Port & Owners by Port */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>Helpers by Port (Top 10)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.helpersByPort} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="port_name" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip formatter={(value) => [value, 'Helpers']} />
                      <Bar dataKey="helper_count" fill="#28a745" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>Yacht Owners by Port (Top 10)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.ownersByPort} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="port_name" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip formatter={(value) => [value, 'Owners']} />
                      <Bar dataKey="owner_count" fill="#0066cc" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Row 3: Status & Registration by Day */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>User Status</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={analytics.userStatus}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        label={({ status, count }) => `${status}: ${count}`}
                      >
                        {analytics.userStatus.map((entry, index) => (
                          <Cell key={entry.status} fill={entry.status === 'active' ? '#28a745' : entry.status === 'suspended' ? '#dc3545' : COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 16 }}>Registrations by Day of Week</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={analytics.registrationByDay}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {analytics.registrationByDay.map((entry, index) => (
                          <Cell key={entry.day} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Search & Filter */}
      <div className="card" style={{ marginBottom: 20 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12 }}>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} style={{ width: 150 }}>
            <option value="">All Types</option>
            <option value="yacht_owner">Yacht Owners</option>
            <option value="helper">Helpers</option>
            <option value="admin">Admins</option>
          </select>
          <select value={portFilter} onChange={(e) => { setPortFilter(e.target.value); setPage(1); }} style={{ width: 250 }}>
            <option value="">All Ports</option>
            {ports.filter(p => p.helper_count > 0 || p.owner_count > 0).map(port => (
              <option key={port.id} value={port.id}>
                {port.name} ({port.helper_count}H / {port.owner_count}O)
              </option>
            ))}
          </select>
          <select value={verifiedFilter} onChange={(e) => { setVerifiedFilter(e.target.value); setPage(1); }} style={{ width: 130 }}>
            <option value="">All Verified</option>
            <option value="true">Verified</option>
            <option value="false">Not Verified</option>
          </select>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </div>

      {/* Users Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={users.length > 0 && selectedUsers.length === users.length}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Yacht</th>
                  <th>Position / Exp</th>
                  <th>Port(s)</th>
                  <th>Verified</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} style={{ background: selectedUsers.includes(user.id) ? '#e3f2fd' : 'transparent' }}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleSelectUser(user.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: '#ddd',
                        backgroundImage: user.profile_photo_url ? `url(${user.profile_photo_url})` : 'none',
                        backgroundSize: 'cover',
                      }} />
                      <span>{user.first_name} {user.last_name}</span>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 500,
                        background: user.user_type === 'helper' ? '#fff3cd' : user.user_type === 'admin' ? '#cce5ff' : '#d4edda',
                        color: user.user_type === 'helper' ? '#856404' : user.user_type === 'admin' ? '#004085' : '#155724',
                      }}>
                        {user.user_type}
                      </span>
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {user.user_type === 'yacht_owner' && user.yacht_name ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontWeight: 500 }}>{user.yacht_name}</span>
                          <span style={{ color: '#666' }}>
                            {user.yacht_type || '-'} {user.yacht_length ? `• ${user.yacht_length}m` : ''}
                          </span>
                        </div>
                      ) : user.user_type === 'yacht_owner' ? (
                        <span style={{ color: '#999' }}>No yacht</span>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {user.user_type === 'helper' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {user.position ? (
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 500,
                              background: user.position === 'deck_greenie' ? '#14b8a615' : '#6366f115',
                              color: user.position === 'deck_greenie' ? '#14b8a6' : '#6366f1',
                              whiteSpace: 'nowrap',
                            }}>
                              {{
                                'deck_greenie': 'Mooring Helper',
                                'deck_deckhand': 'Deckhand',
                                'deck_bosun': 'Bosun',
                                'deck_mate': 'Mate',
                                'deck_captain': 'Captain',
                                'interior_steward': 'Steward/ess',
                                'interior_chief_stew': 'Chief Stew',
                                'engineering_engineer': 'Engineer',
                                'engineering_chief': 'Chief Eng',
                                'galley_cook': 'Chef',
                              }[user.position] || user.position}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: 11 }}>No position</span>
                          )}
                          {user.experience_years > 0 && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: 10,
                              fontSize: 10,
                              background: '#f59e0b15',
                              color: '#f59e0b',
                            }}>
                              {user.experience_years} yr{user.experience_years !== 1 ? 's' : ''} exp
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 12, color: '#666' }}>
                      {user.service_ports || user.home_ports || '-'}
                    </td>
                    <td>
                      {user.email_verified ? (
                        <span style={{ color: '#28a745', fontSize: 16 }} title="Email verified">✓</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#dc3545', fontSize: 16 }} title="Email not verified">✗</span>
                          <button
                            onClick={() => verifyEmail(user.id)}
                            className="btn"
                            style={{ padding: '2px 6px', fontSize: 10, background: '#28a745', color: 'white' }}
                            title="Manually verify email"
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => resendVerification(user.id)}
                            className="btn"
                            style={{ padding: '2px 6px', fontSize: 10, background: '#6c757d', color: 'white' }}
                            title="Resend verification email"
                          >
                            Resend
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 500,
                        background: user.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: user.status === 'active' ? '#155724' : '#721c24',
                      }}>
                        {user.status}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Link
                          to={`/users/${user.id}`}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            background: '#0066cc',
                            color: 'white',
                            borderRadius: 6,
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                        >
                          View
                        </Link>
                        {user.status === 'active' ? (
                          <button className="btn btn-danger" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => updateStatus(user.id, 'suspended')}>
                            Suspend
                          </button>
                        ) : (
                          <button className="btn btn-success" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => updateStatus(user.id, 'active')}>
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
              <span style={{ color: '#666', fontSize: 14 }}>
                {selectedUsers.length > 0 ? (
                  <span style={{ color: '#0066cc', fontWeight: 500 }}>{selectedUsers.length} selected | </span>
                ) : null}
                Showing {users.length} of {total} users
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: '#eee' }}>
                  Previous
                </button>
                <span style={{ padding: '10px 16px' }}>Page {page} of {totalPages}</span>
                <button className="btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ background: '#eee' }}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ width: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Send Email</h2>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#666' }}
              >
                &times;
              </button>
            </div>

            {/* Recipients */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Recipients ({selectedUsersList.length})
              </label>
              <div style={{
                background: '#f8f9fa',
                padding: 12,
                borderRadius: 8,
                maxHeight: 100,
                overflow: 'auto',
                fontSize: 13,
              }}>
                {selectedUsersList.map((user, i) => (
                  <span key={user.id}>
                    {user.first_name} {user.last_name} &lt;{user.email}&gt;
                    {i < selectedUsersList.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Subject</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
                style={{ width: '100%' }}
              />
            </div>

            {/* Body */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Message
                <span style={{ fontWeight: 400, color: '#666', fontSize: 12, marginLeft: 8 }}>
                  (Use {'{first_name}'}, {'{last_name}'}, {'{email}'} for personalization)
                </span>
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your message..."
                rows={8}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn"
                onClick={() => setShowEmailModal(false)}
                style={{ background: '#eee' }}
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
              >
                {sendingEmail ? 'Sending...' : `Send to ${selectedUsersList.length} User${selectedUsersList.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
